import { useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { useReferenceData } from '@/features/pipeline/useReferenceData';
import { useAddComment, useTimeline, type TimelineLinkType } from './api';

const KIND_DOT: Record<string, string> = {
  field: 'bg-vega-info',
  committee: 'bg-vega-gold',
  task: 'bg-vega-accent',
  comment: 'bg-vega-muted',
};

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Seção Timeline (DOCUMENTACAO.md §4.8) — feed projetado + comentário solto. Deal ou Investment. */
export function TimelineSection({ linkType, linkId }: { linkType: TimelineLinkType; linkId: string }) {
  const { data, isLoading } = useTimeline(linkType, linkId);
  const addComment = useAddComment(linkType, linkId);
  const { data: ref } = useReferenceData();
  const [text, setText] = useState('');

  const userName = (id: string | null) =>
    !id ? '' : (ref?.users.find((u) => u.id === id)?.name ?? ref?.leadUsers.find((u) => u.id === id)?.name ?? '');

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    await addComment.mutateAsync(t);
    setText('');
  };

  const items = data ?? [];

  return (
    <section className="mb-6">
      <h3 className="mb-2 border-b border-vega-line pb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-vega-muted">
        Timeline
      </h3>

      <div className="mb-3 flex gap-2">
        <input
          className="flex-1 rounded border border-vega-line bg-vega-surface px-2.5 py-1.5 text-[12.5px] text-vega-ink outline-none"
          placeholder="Adicionar comentário…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
        />
        <button type="button" disabled={!text.trim() || addComment.isPending} onClick={() => void submit()} className="flex items-center gap-1 bg-vega-ink px-2.5 py-1.5 text-[12px] text-vega-surface disabled:opacity-40">
          <MessageSquarePlus size={14} /> Comentar
        </button>
      </div>

      {isLoading ? (
        <p className="text-[12.5px] text-vega-muted-2">Carregando…</p>
      ) : items.length === 0 ? (
        <p className="text-[12.5px] text-vega-muted-2">Sem atividade ainda.</p>
      ) : (
        <ol className="flex flex-col gap-0">
          {items.map((it, i) => (
            // Achado F16: key={i} pura quebra em listas que crescem por prepend (novo comentário
            // reordena); chave composta pelo conteúdo evita remontar/trocar item errado.
            <li
              key={`${it.occurredAtUtc}-${it.kind}-${i}`}
              className="flex gap-3 border-l border-vega-line-2 pb-3 pl-3 last:border-l-transparent"
            >
              <span className={`mt-1 -ml-[15px] h-2 w-2 flex-shrink-0 rounded-full ${KIND_DOT[it.kind] ?? 'bg-vega-muted'}`} />
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-[12.5px] text-vega-ink">{it.summary}</span>
                  <span className="font-mono text-[10.5px] text-vega-muted-2">{when(it.occurredAtUtc)}</span>
                </div>
                {it.detail && <div className="text-[11.5px] text-vega-muted">{it.detail}</div>}
                {it.comment && <div className="mt-0.5 text-[12px] italic text-vega-ink-2">“{it.comment}”</div>}
                {it.actorUserId && <div className="text-[10.5px] text-vega-muted-2">{userName(it.actorUserId)}</div>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
